import React from "react";
import { Box, Typography } from "@mui/material";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import NewspaperRoundedIcon from "@mui/icons-material/NewspaperRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import FlagRoundedIcon from "@mui/icons-material/FlagRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";

// ─── Theme: matches existing ALMR/QuantDashboard palette ───────────────────

const NAVY      = "#002060"; // brand navy — headings
const NAVY_2    = "#0A3270"; // body text
const NAVY_3    = "#1A4A9A"; // labels / sub-headings
const NAVY_TINT = "#EEF2F8"; // very soft navy fill

const PAGE_BG = "#EEF2F8";
const CARD    = "#FFFFFF";
const BORDER  = "#C8D4E8";
const HAIR    = "#E2E8F0";
const SOFT    = "#F4F6FB";

const T1 = NAVY;     // primary text / headings / big numbers
const T2 = NAVY_2;   // secondary text
const T3 = NAVY_3;   // labels (dark navy, no gray)

// Accent palette — same as existing QuantDashboardRenderer
const TEAL   = "#0284C7";
const PURPLE = "#6D28D9";
const AMBER  = "#D97706";
const GREEN  = "#059669";
const RED    = "#DC2626";
const BLUE   = "#1D4ED8";

const BAR_GRAD  = `linear-gradient(90deg, ${TEAL}, ${PURPLE})`;
const BAR_TRACK = "#D6E0F0";

// Direction shortcuts
const POS = GREEN;
const NEG = RED;

const FONT = "'Inter','DM Sans','Segoe UI',sans-serif";
const MONO = "'SF Mono','Fira Code',monospace";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface IPOQuantData {
  meta: {
    ticker: string;
    company_name: string;
    sector: string;
    ipo_date: string;
    report_date: string;
    days_since_ipo: number;
  };
  price_action: {
    last_price: number;
    day_change_pct: number;
    ipo_return_pct: number;
    five_day_return_pct: number;
    intraday_low: number;
    intraday_high: number;
    relative_volume: number;
    vwap: number;
  };
  technical_analysis: {
    momentum_score: number;
    technical_grade: string;
    trend_status: string;
    vwap_position: string;
    ema_5_trend: string;
    ema_10_trend: string;
    rsi_14: number;
    macd_signal: string;
    gap_retention: string;
    float_rotation: string;
    volatility_state: string;
    liquidity_quality: string;
  };
  institutional_flow: {
    institutional_behavior: string;
    accumulation_signal: string;
    distribution_risk: string;
    volume_structure: string;
  };
  news_catalysts: string[];
  risk_factors: string[];
  trade_setup: {
    trade_bias: string;
    entry_zone: string;
    breakout_trigger: string;
    stop_loss: number;
    target_1: number;
    target_2: number;
    max_hold_days: number;
  };
  ai_summary: {
    market_structure: string;
    short_term_view: string;
    continuation_probability: number;
    risk_reward_rating: string;
    confidence_score: number;
    fund_manager_summary: string;
  };
  /** Optional headline verdict, e.g. "BUY" / "SELL" / "HOLD". */
  final_signal?: string;
  /** Optional headline rationale paragraph rendered alongside `final_signal`. */
  summary?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const isIPOQuantFormat = (data: any): boolean =>
  data !== null &&
  typeof data === "object" &&
  !Array.isArray(data) &&
  "meta" in data &&
  "price_action" in data &&
  "technical_analysis" in data &&
  "trade_setup" in data;

const fmt    = (v: any) => (typeof v === "number" ? v.toFixed(2) : String(v ?? "—"));
const fmtPct = (v: number) => `${v >= 0 ? "+" : ""}${(v ?? 0).toFixed(2)}%`;

/**
 * API fields can be either reader-ready prose or machine-style enum values
 * (for example, `INTERMEDIATE_UPTREND_WITH_SHORT_TERM_PULLBACK`). Keep the
 * source value intact while making every dashboard label readable.
 */
const formatDisplayText = (value: unknown, fallback = "—") => {
  const text = String(value ?? "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!text) return fallback;

  const isAllCaps = text === text.toUpperCase() && text !== text.toLowerCase();
  let formatted = isAllCaps
    ? `${text.charAt(0)}${text.slice(1).toLowerCase()}`
    : text;

  formatted = formatted
    .replace(/\b(ipo|vwap|macd|rsi|ai)\b/gi, (_, acronym) => acronym.toUpperCase())
    .replace(/\bema\s?(\d+)\b/gi, (_, period) => `EMA ${period}`);

  return formatted;
};

// Signal classifier — same logic as existing QuantDashboardRenderer
const sigCol = (v: string = "") => {
  const s = v.toLowerCase();
  if (s.includes("buy") || (s.includes("bullish") && !s.includes("speculative")) ||
      s.includes("accumulation") || s.includes("constructive") || s.includes("favorable") ||
      s.includes("healthy") || s.includes("attractive") || s.includes("strong"))
    return GREEN;
  if (s.includes("avoid") || s.includes("sell") || s.includes("bearish") ||
      s.includes("weak") || s.includes("distribution") || s.includes("extreme"))
    return RED;
  if (s.includes("momentum") || s.includes("acceleration") || s.includes("outperform") ||
      s.includes("expansion") || s.includes("breakout"))
    return TEAL;
  if (s.includes("risk") || s.includes("speculative") || s.includes("volatile") ||
      s.includes("moderate") || s.includes("elevated") || s.includes("high"))
    return AMBER;
  return PURPLE;
};

const gradeCol = (g: string = "") => {
  const c = g.trim().charAt(0).toUpperCase();
  if (c === "A") return GREEN;
  if (c === "B") return TEAL;
  if (c === "C") return AMBER;
  if (c === "D" || c === "F") return PURPLE;
  return PURPLE;
};

const scCol = (n: number) => (n >= 70 ? GREEN : n >= 50 ? TEAL : n >= 30 ? AMBER : RED);

// Headline verdict color — covers BUY / SELL / HOLD / AVOID / WATCH etc.
const verdictCol = (s: string = "") => {
  const v = s.trim().toUpperCase();
  if (v === "BUY" || v === "STRONG BUY" || v === "ACCUMULATE") return GREEN;
  if (v === "SELL" || v === "STRONG SELL" || v === "AVOID")    return RED;
  if (v === "HOLD" || v === "WATCH" || v === "MONITOR")        return AMBER;
  return PURPLE;
};

// ─── Primitives ───────────────────────────────────────────────────────────────

const Chip: React.FC<{ label: string; color?: string; solid?: boolean; muted?: boolean; size?: "sm" | "md" | "lg" }> = ({
  label, color = NAVY, solid, muted, size = "sm",
}) => {
  const dims = size === "lg"
    ? { px: 2.2, py: 0.9, fontSize: "0.95rem", border: 1.5 }
    : size === "md"
      ? { px: 1.8, py: 0.65, fontSize: "0.82rem", border: 1.2 }
      : { px: 1.4, py: 0.45, fontSize: "0.7rem", border: 1 };
  return (
    <Box sx={{
      px: dims.px, py: dims.py, borderRadius: 99,
      background: solid ? color : (muted ? "transparent" : `${color}14`),
      border: `${dims.border}px solid ${solid ? color : (muted ? BORDER : `${color}40`)}`,
      display: "inline-flex", alignItems: "center",
    }}>
      <Typography sx={{
        fontSize: dims.fontSize, fontWeight: 800, fontFamily: FONT,
        color: solid ? "#FFFFFF" : (muted ? T2 : color), letterSpacing: "0.02em",
      }}>
        {label}
      </Typography>
    </Box>
  );
};

const KpiCard: React.FC<{ label: string; value: string; sub: string; color?: string; icon?: React.ReactNode }> = ({
  label, value, sub, color = NAVY, icon,
}) => (
  <Box sx={{
    background: CARD, borderRadius: 3, border: `1px solid ${BORDER}`,
    p: 2.5, transition: "all 0.15s",
    "&:hover": { borderColor: `${NAVY}55`, transform: "translateY(-1px)" },
  }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.4 }}>
      <Typography sx={{ fontSize: "0.7rem", color: T3, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: FONT }}>
        {label}
      </Typography>
      {icon && (
        <Box sx={{ width: 26, height: 26, borderRadius: 1.5, background: NAVY_TINT, display: "flex", alignItems: "center", justifyContent: "center", color: NAVY }}>
          {icon}
        </Box>
      )}
    </Box>
    <Typography sx={{ fontSize: "1.85rem", fontWeight: 700, color, lineHeight: 1, mb: 1, fontFamily: FONT, letterSpacing: "-0.025em" }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: "0.78rem", color: T2, fontWeight: 600, fontFamily: FONT }}>
      {sub}
    </Typography>
  </Box>
);

const Card: React.FC<{ title?: string; subtitle?: string; icon?: React.ReactNode; iconColor?: string; badge?: React.ReactNode; children: React.ReactNode }> = ({
  title, subtitle, icon, iconColor = TEAL, badge, children,
}) => (
  <Box sx={{
    background: CARD, borderRadius: 3, border: `1px solid ${BORDER}`,
    overflow: "hidden", height: "100%",
  }}>
    {(title || icon || badge) && (
      <Box sx={{ px: 3, pt: 2.4, pb: 1.6, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.4, minWidth: 0 }}>
          {icon && (
            <Box sx={{ width: 32, height: 32, borderRadius: 2, background: `${iconColor}14`, color: iconColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            {title && <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: T1, fontFamily: FONT, letterSpacing: "-0.015em" }}>{title}</Typography>}
            {subtitle && <Typography sx={{ fontSize: "0.76rem", color: T3, fontWeight: 600, mt: 0.2, fontFamily: FONT }}>{subtitle}</Typography>}
          </Box>
        </Box>
        {badge}
      </Box>
    )}
    <Box sx={{ px: 3, pb: 2.8 }}>{children}</Box>
  </Box>
);

const DataRow: React.FC<{ label: string; value: string | number; color?: string; mono?: boolean; last?: boolean; weight?: number }> = ({
  label, value, color = T1, mono, last, weight = 800,
}) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, py: 1.1, borderBottom: last ? "none" : `1px solid ${HAIR}` }}>
    <Typography sx={{ fontSize: "0.78rem", color: T2, fontWeight: 700, fontFamily: FONT }}>{label}</Typography>
    <Typography sx={{ fontSize: "0.82rem", fontWeight: weight, color, textAlign: "right", lineHeight: 1.4, fontFamily: mono ? MONO : FONT, maxWidth: "62%" }}>
      {value}
    </Typography>
  </Box>
);

const Bullet: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color = NAVY }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.3, py: 1, borderBottom: `1px solid ${HAIR}`, "&:last-child": { borderBottom: "none" } }}>
    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: color, mt: 0.7, flexShrink: 0, boxShadow: `0 0 0 3px ${color}22` }} />
    <Typography sx={{ fontSize: "0.82rem", color: T1, lineHeight: 1.6, fontFamily: FONT, fontWeight: 600 }}>
      {children}
    </Typography>
  </Box>
);

// ─── Progress bar (teal → purple gradient, matches ALMR) ─────────────────────

const Bar: React.FC<{ label: string; value: number; max?: number; suffix?: string }> = ({
  label, value, max = 100, suffix,
}) => {
  const pct = Math.min(Math.max((value / max) * 100, 0), 100);
  return (
    <Box sx={{ mb: 1.8 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.6 }}>
        <Typography sx={{ fontSize: "0.78rem", color: T2, fontWeight: 700, fontFamily: FONT }}>{label}</Typography>
        <Typography sx={{ fontSize: "0.82rem", fontWeight: 900, color: scCol(pct), fontFamily: MONO }}>
          {value}{suffix || ""}
        </Typography>
      </Box>
      <Box sx={{ height: 8, background: BAR_TRACK, borderRadius: 99, overflow: "hidden" }}>
        <Box sx={{ width: `${pct}%`, height: "100%", background: BAR_GRAD, borderRadius: 99, transition: "width 0.4s" }} />
      </Box>
    </Box>
  );
};

// ─── Circular gauge (score-colored ring) ──────────────────────────────────────

const Gauge: React.FC<{ value: number; max?: number; label: string; size?: number }> = ({
  value, max = 100, label, size = 150,
}) => {
  const r = (size - 16) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.min(Math.max(value / max, 0), 1);
  const ringColor = scCol(value);
  return (
    <Box sx={{ position: "relative", width: size, height: size, mx: "auto" }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke={BAR_TRACK} strokeWidth={10} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke={ringColor} strokeWidth={10} fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset 0.5s" }}
        />
      </svg>
      <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontSize: "2.2rem", fontWeight: 900, color: ringColor, lineHeight: 1, fontFamily: FONT, letterSpacing: "-0.025em" }}>
          {value}
        </Typography>
        <Typography sx={{ fontSize: "0.62rem", color: T3, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.15em", mt: 0.6, fontFamily: FONT }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Trade Setup pill (role-colored: blue/teal entry, red stop, green targets) ───

const TradePill: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => (
  <Box sx={{
    background: `${color}0C`,
    border: `1.5px solid ${color}33`,
    borderRadius: 2.5, p: 1.6, textAlign: "center",
  }}>
    <Typography sx={{ fontSize: "0.62rem", color, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.09em", mb: 0.6, fontFamily: FONT }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "1rem", fontWeight: 900, color, fontFamily: MONO, letterSpacing: "-0.01em" }}>
      {value}
    </Typography>
  </Box>
);

// ─── Main ─────────────────────────────────────────────────────────────────────

const IPOQuantDashboardRenderer: React.FC<{ data: IPOQuantData }> = ({ data }) => {
  const { meta, price_action: pa, technical_analysis: ta, institutional_flow: inst, news_catalysts, risk_factors, trade_setup: ts, ai_summary: ai } = data;

  const pos     = (pa?.day_change_pct ?? 0) >= 0;
  const ipoPos  = (pa?.ipo_return_pct ?? 0) >= 0;
  const fivePos = (pa?.five_day_return_pct ?? 0) >= 0;
  const aboveVwap = (pa?.last_price ?? 0) >= (pa?.vwap ?? 0);

  const signal = (data.final_signal || "").trim();
  const signalColor = verdictCol(signal);

  return (
    <Box sx={{ background: PAGE_BG, fontFamily: FONT, borderRadius: 3, p: { xs: 1.5, md: 2 } }}>

      {/* ─── VERDICT BANNER ─────────────────────────────────────────────
          Prominent top-of-dashboard headline: final_signal + summary. */}
      {(signal || data.summary) && (
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            gap: { xs: 2, md: 3 },
            flexDirection: { xs: "column", md: "row" },
            background: `linear-gradient(135deg, ${signalColor}10, ${signalColor}04)`,
            border: `1.5px solid ${signalColor}55`,
            borderLeft: `6px solid ${signalColor}`,
            borderRadius: 3,
            p: { xs: 2, md: 2.5 },
            mb: 2,
          }}
        >
          {signal && (
            <Box sx={{ flexShrink: 0, textAlign: { xs: "left", md: "center" }, minWidth: { md: 140 } }}>
              <Typography
                sx={{
                  fontSize: "0.66rem",
                  color: T3,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.09em",
                  fontFamily: FONT,
                  mb: 0.6,
                }}
              >
                Final Signal
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  px: 2.4,
                  py: 1,
                  borderRadius: 2,
                  background: signalColor,
                  boxShadow: `0 4px 12px ${signalColor}55`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "1.4rem", md: "1.6rem" },
                    fontWeight: 900,
                    color: "#FFFFFF",
                    letterSpacing: "0.06em",
                    fontFamily: FONT,
                    lineHeight: 1,
                  }}
                >
                  {signal.toUpperCase()}
                </Typography>
              </Box>
            </Box>
          )}

          {data.summary && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "0.66rem",
                  color: T3,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.09em",
                  fontFamily: FONT,
                  mb: 0.6,
                }}
              >
                Rationale
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.88rem",
                  color: T1,
                  fontWeight: 600,
                  lineHeight: 1.65,
                  fontFamily: FONT,
                }}
              >
                {formatDisplayText(data.summary, "")}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* ─── HERO ─────────────────────────────────────────────────────── */}
      <Box sx={{ background: CARD, borderRadius: 3, border: `1px solid ${BORDER}`, p: { xs: 2.5, md: 3.5 }, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 3, flexWrap: "wrap" }}>

          {/* Left */}
          <Box sx={{ flex: 1, minWidth: 320 }}>
            <Typography sx={{ fontSize: { xs: "1.7rem", md: "2.3rem" }, fontWeight: 900, color: NAVY, letterSpacing: "-0.03em", lineHeight: 1.1, fontFamily: FONT, mb: 1.6 }}>
              {meta?.company_name}
            </Typography>

            <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap", mb: 2 }}>
              <Chip label={meta?.ticker} color={NAVY} solid />
              {signal && <Chip label={signal.toUpperCase()} color={signalColor} solid />}
              {meta?.sector && <Chip label={meta.sector} color={PURPLE} />}
              {typeof meta?.days_since_ipo === "number" && <Chip label={`${meta.days_since_ipo} Days Since IPO`} color={TEAL} />}
              {meta?.ipo_date && <Chip label={`IPO: ${meta.ipo_date}`} muted />}
            </Box>

            <Typography sx={{ fontSize: "0.88rem", color: T2, lineHeight: 1.65, maxWidth: 640, fontWeight: 500, fontFamily: FONT }}>
              {formatDisplayText(ai?.market_structure, "")}
            </Typography>
          </Box>

          {/* Right — price + AI view */}
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-start" }}>
            <Box sx={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 3, p: 2.2, minWidth: 160 }}>
              <Typography sx={{ fontSize: "0.66rem", color: T3, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.09em", fontFamily: FONT, mb: 0.6 }}>
                Current Price
              </Typography>
              <Typography sx={{ fontSize: "1.85rem", fontWeight: 900, color: NAVY, letterSpacing: "-0.025em", lineHeight: 1.1, fontFamily: FONT }}>
                ${fmt(pa?.last_price)}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mt: 0.8 }}>
                {pos
                  ? <TrendingUpRoundedIcon sx={{ fontSize: 16, color: POS }} />
                  : <TrendingDownRoundedIcon sx={{ fontSize: 16, color: NEG }} />}
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 800, color: pos ? POS : NEG, fontFamily: FONT }}>
                  {fmtPct(pa?.day_change_pct)} Today
                </Typography>
              </Box>
            </Box>

            <Box sx={{
              background: `linear-gradient(135deg, ${TEAL}14, ${PURPLE}14)`,
              border: `1px solid ${TEAL}33`,
              borderRadius: 3, p: 2.2, minWidth: 220,
            }}>
              <Typography sx={{ fontSize: "0.66rem", color: T2, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.09em", fontFamily: FONT, mb: 0.8 }}>
                AI Short-Term View
              </Typography>
              <Typography sx={{ fontSize: "1rem", fontWeight: 900, color: sigCol(ai?.risk_reward_rating || ""), lineHeight: 1.25, mb: 1, fontFamily: FONT }}>
                {formatDisplayText(ai?.risk_reward_rating, "")}
              </Typography>
              <Typography sx={{ fontSize: "0.76rem", color: T2, fontWeight: 700, mb: 1.2, fontFamily: FONT }}>
                {ai?.confidence_score}% Confidence
              </Typography>
              <Box sx={{ height: 5, background: `${TEAL}20`, borderRadius: 99, overflow: "hidden" }}>
                <Box sx={{ width: `${ai?.confidence_score ?? 0}%`, height: "100%", background: BAR_GRAD, borderRadius: 99 }} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ─── PRICE KPI ROW ─────────────────────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4,1fr)" }, gap: 2, mb: 2 }}>
        <KpiCard label="IPO Return"   value={fmtPct(pa?.ipo_return_pct)}      sub="From offer price"    color={ipoPos ? POS : NEG} />
        <KpiCard label="5-Day Return" value={fmtPct(pa?.five_day_return_pct)} sub="Recent momentum"     color={fivePos ? POS : NEG} />
        <KpiCard label="VWAP"         value={`$${fmt(pa?.vwap)}`}             sub={aboveVwap ? "Trading above" : "Trading below"} />
        <KpiCard label="Rel. Volume"  value={`${fmt(pa?.relative_volume)}x`}  sub="Participation" />
      </Box>

      {/* ─── METRIC ROW ────────────────────────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4,1fr)" }, gap: 2, mb: 2 }}>
        <Box sx={{
          background: CARD, borderRadius: 3, border: `1px solid ${BORDER}`, p: 2.5,
          transition: "all 0.15s",
          "&:hover": { borderColor: `${NAVY}55`, transform: "translateY(-1px)" },
        }}>
          <Typography sx={{ fontSize: "0.7rem", color: T3, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.4, fontFamily: FONT }}>
            Technical Grade
          </Typography>
          <Typography sx={{ fontSize: "2.6rem", fontWeight: 700, color: gradeCol(ta?.technical_grade), lineHeight: 1, mb: 1, fontFamily: FONT, letterSpacing: "-0.03em" }}>
            {ta?.technical_grade}
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: T2, fontWeight: 700, fontFamily: FONT }}>Institutional Quality</Typography>
        </Box>

        <Box sx={{
          background: CARD, borderRadius: 3, border: `1px solid ${BORDER}`, p: 2.5,
          transition: "all 0.15s",
          "&:hover": { borderColor: `${NAVY}55`, transform: "translateY(-1px)" },
        }}>
          <Typography sx={{ fontSize: "0.7rem", color: T3, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.4, fontFamily: FONT }}>
            Momentum Score
          </Typography>
          <Typography sx={{ fontSize: "2.6rem", fontWeight: 700, color: scCol(ta?.momentum_score), lineHeight: 1, mb: 1, fontFamily: FONT, letterSpacing: "-0.025em" }}>
            {ta?.momentum_score}
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: T2, fontWeight: 700, fontFamily: FONT, lineHeight: 1.4 }}>
            {formatDisplayText(ta?.trend_status).split(" ").slice(0, 4).join(" ")}
          </Typography>
        </Box>

        <Box sx={{
          background: CARD, borderRadius: 3, border: `1px solid ${BORDER}`, p: 2.5,
          transition: "all 0.15s",
          "&:hover": { borderColor: `${NAVY}55`, transform: "translateY(-1px)" },
        }}>
          <Typography sx={{ fontSize: "0.7rem", color: T3, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.4, fontFamily: FONT }}>
            RSI (14)
          </Typography>
          <Typography sx={{ fontSize: "2.6rem", fontWeight: 700, color: scCol(ta?.rsi_14), lineHeight: 1, mb: 1, fontFamily: FONT, letterSpacing: "-0.025em" }}>
            {ta?.rsi_14}
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: T2, fontWeight: 700, fontFamily: FONT }}>
            {ta?.rsi_14 >= 70 ? "Overbought zone" : ta?.rsi_14 >= 50 ? "Bullish momentum" : ta?.rsi_14 >= 30 ? "Neutral / weak" : "Oversold zone"}
          </Typography>
        </Box>

        <Box sx={{
          background: CARD, borderRadius: 3, border: `1px solid ${BORDER}`, p: 2.5,
          transition: "all 0.15s",
          "&:hover": { borderColor: `${NAVY}55`, transform: "translateY(-1px)" },
        }}>
          <Typography sx={{ fontSize: "0.7rem", color: T3, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.4, fontFamily: FONT }}>
            Continuation Probability
          </Typography>
          <Typography sx={{ fontSize: "2.6rem", fontWeight: 700, color: scCol(ai?.continuation_probability), lineHeight: 1, mb: 1, fontFamily: FONT, letterSpacing: "-0.025em" }}>
            {ai?.continuation_probability}%
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: T2, fontWeight: 700, fontFamily: FONT }}>Short-term outlook</Typography>
        </Box>
      </Box>

      {/* ─── TECHNICAL ANALYSIS + INSTITUTIONAL FLOW ─────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 2 }}>
        <Card
          title="Technical Analysis"
          subtitle="Trend, momentum & participation signals"
          icon={<ShowChartRoundedIcon sx={{ fontSize: 18 }} />}
          iconColor={TEAL}
          badge={<Chip label={ta?.technical_grade} color={gradeCol(ta?.technical_grade)} size="lg" />}
        >
          {/* Intraday range — color-coded low/vwap/high */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", background: SOFT, borderRadius: 2.5, mb: 2, overflow: "hidden" }}>
            {[
              { label: "Low",  v: pa?.intraday_low,  color: RED    },
              { label: "VWAP", v: pa?.vwap,          color: PURPLE },
              { label: "High", v: pa?.intraday_high, color: GREEN  },
            ].map((c, i) => (
              <Box key={i} sx={{ p: 1.6, textAlign: "center", borderRight: i < 2 ? `1px solid ${BORDER}` : "none" }}>
                <Typography sx={{ fontSize: "0.62rem", color: T3, textTransform: "uppercase", letterSpacing: "0.09em", fontWeight: 800, fontFamily: FONT, mb: 0.6 }}>
                  {c.label}
                </Typography>
                <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, color: c.color, fontFamily: MONO, letterSpacing: "-0.01em" }}>
                  ${fmt(c.v)}
                </Typography>
              </Box>
            ))}
          </Box>

          <DataRow label="Trend Status"      value={formatDisplayText(ta?.trend_status)}      color="#092714" weight={500} />
          <DataRow label="VWAP Position"     value={formatDisplayText(ta?.vwap_position)}     color="#092714" weight={500} />
          <DataRow label="EMA 5 Trend"       value={formatDisplayText(ta?.ema_5_trend)}       color="#092714" weight={500} />
          <DataRow label="EMA 10 Trend"      value={formatDisplayText(ta?.ema_10_trend)}      color="#092714" weight={500} />
          <DataRow label="MACD Signal"       value={formatDisplayText(ta?.macd_signal)}       color="#092714" weight={500} />
          <DataRow label="Gap Retention"     value={formatDisplayText(ta?.gap_retention)}     color="#092714" weight={500} />
          <DataRow label="Float Rotation"    value={formatDisplayText(ta?.float_rotation)}    color="#092714" weight={500} />
          <DataRow label="Volatility State"  value={formatDisplayText(ta?.volatility_state)}  color="#092714" weight={500} />
          <DataRow label="Liquidity Quality" value={formatDisplayText(ta?.liquidity_quality)} color="#092714" weight={500} last />
        </Card>

        <Card
          title="Institutional Flow"
          subtitle="Smart money behavior & volume structure"
          icon={<AccountBalanceRoundedIcon sx={{ fontSize: 18 }} />}
          iconColor={BLUE}
          badge={<Chip label="FLOW" color={sigCol(inst?.accumulation_signal || "")} />}
        >
          <Box sx={{ background: `${BLUE}08`, borderRadius: 2, p: 1.8, mb: 1.6, border: `1px solid ${BLUE}22` }}>
            <Typography sx={{ fontSize: "0.66rem", color: BLUE, textTransform: "uppercase", letterSpacing: "0.09em", fontWeight: 800, fontFamily: FONT, mb: 0.6 }}>
              Institutional Behavior
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: T1, fontWeight: 600, lineHeight: 1.6, fontFamily: FONT }}>
              {formatDisplayText(inst?.institutional_behavior, "")}
            </Typography>
          </Box>

          <Box sx={{ background: `${GREEN}08`, borderRadius: 2, p: 1.8, mb: 1.6, border: `1px solid ${GREEN}22` }}>
            <Typography sx={{ fontSize: "0.66rem", color: GREEN, textTransform: "uppercase", letterSpacing: "0.09em", fontWeight: 800, fontFamily: FONT, mb: 0.6 }}>
              Accumulation Signal
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: T1, fontWeight: 600, lineHeight: 1.6, fontFamily: FONT }}>
              {formatDisplayText(inst?.accumulation_signal, "")}
            </Typography>
          </Box>

          <DataRow label="Distribution Risk" value={formatDisplayText(inst?.distribution_risk)} color={sigCol(inst?.distribution_risk || "")} />

          <Box sx={{ mt: 1.6 }}>
            <Typography sx={{ fontSize: "0.66rem", color: T3, textTransform: "uppercase", letterSpacing: "0.09em", fontWeight: 800, fontFamily: FONT, mb: 0.6 }}>
              Volume Structure
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: T1, fontWeight: 600, lineHeight: 1.6, fontFamily: FONT }}>
              {formatDisplayText(inst?.volume_structure, "")}
            </Typography>
          </Box>
        </Card>
      </Box>

      {/* ─── CATALYSTS + RISKS ──────────────────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 2 }}>
        <Card
          title="News Catalysts"
          subtitle="Drivers of momentum & participation"
          icon={<NewspaperRoundedIcon sx={{ fontSize: 18 }} />}
          iconColor={GREEN}
          badge={news_catalysts?.length ? <Chip label={`${news_catalysts.length}`} color={GREEN} /> : undefined}
        >
          {news_catalysts?.map((item, idx) => <Bullet key={idx} color={GREEN}>{item}</Bullet>)}
          {(!news_catalysts || news_catalysts.length === 0) && (
            <Typography sx={{ fontSize: "0.82rem", color: T3, fontStyle: "italic" }}>No catalysts identified.</Typography>
          )}
        </Card>

        <Card
          title="Risk Factors"
          subtitle="Key risks to monitor"
          icon={<WarningAmberRoundedIcon sx={{ fontSize: 18 }} />}
          iconColor={AMBER}
          badge={risk_factors?.length ? <Chip label={`${risk_factors.length}`} color={AMBER} /> : undefined}
        >
          {risk_factors?.map((item, idx) => <Bullet key={idx} color={AMBER}>{item}</Bullet>)}
          {(!risk_factors || risk_factors.length === 0) && (
            <Typography sx={{ fontSize: "0.82rem", color: T3, fontStyle: "italic" }}>No major risks identified.</Typography>
          )}
        </Card>
      </Box>

      {/* ─── TRADE SETUP ─────────────────────────────────────────────── */}
      <Box sx={{ mb: 2 }}>
        <Card
          title="Trade Setup"
          subtitle="Actionable entry, stop & target levels"
          icon={<FlagRoundedIcon sx={{ fontSize: 18 }} />}
          iconColor={PURPLE}
          badge={<Chip label={formatDisplayText(ts?.trade_bias)} color={sigCol(ts?.trade_bias || "")} />}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3,1fr)", md: "repeat(6,1fr)" }, gap: 1.6 }}>
            <TradePill label="Entry Zone" value={String(ts?.entry_zone)} color={BLUE} />
            <TradePill label="Breakout"   value={ts?.breakout_trigger?.match(/\d+(\.\d+)?/)?.[0] ? `$${ts?.breakout_trigger?.match(/\d+(\.\d+)?/)?.[0]}` : "—"} color={TEAL} />
            <TradePill label="Stop Loss"  value={`$${fmt(ts?.stop_loss)}`} color={RED} />
            <TradePill label="Target 1"   value={`$${fmt(ts?.target_1)}`} color={GREEN} />
            <TradePill label="Target 2"   value={`$${fmt(ts?.target_2)}`} color={GREEN} />
            <TradePill label="Max Hold"   value={`${ts?.max_hold_days}d`}  color={PURPLE} />
          </Box>

          {ts?.breakout_trigger && (
            <Box sx={{ mt: 2, p: 1.6, background: `${TEAL}08`, borderRadius: 2, borderLeft: `3px solid ${TEAL}` }}>
              <Typography sx={{ fontSize: "0.66rem", color: TEAL, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: FONT, mb: 0.4 }}>
                Breakout Trigger
              </Typography>
              <Typography sx={{ fontSize: "0.82rem", color: T1, fontWeight: 700, lineHeight: 1.5, fontFamily: FONT }}>
                {formatDisplayText(ts.breakout_trigger, "")}
              </Typography>
            </Box>
          )}
        </Card>
      </Box>

      {/* ─── AI SUMMARY ─────────────────────────────────────────────── */}
      <Card
        title="AI Summary"
        subtitle="Master institutional view & conviction"
        icon={<InsightsRoundedIcon sx={{ fontSize: 18 }} />}
        iconColor={PURPLE}
        badge={<Chip label={formatDisplayText(ai?.risk_reward_rating)} color={sigCol(ai?.risk_reward_rating || "")} />}
      >
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "180px minmax(0, 1fr) minmax(280px, 320px)" }, gap: 3, alignItems: "start", minWidth: 0 }}>
          <Box sx={{ textAlign: "center", minWidth: 0 }}>
            <Gauge value={ai?.confidence_score ?? 0} label="Confidence" />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Bar label="Continuation Probability" value={ai?.continuation_probability ?? 0} suffix="%" />
            <Bar label="Confidence Score"         value={ai?.confidence_score ?? 0} />
            <Bar label="Momentum Score"           value={ta?.momentum_score ?? 0} />
            <Bar label="RSI (14)"                 value={ta?.rsi_14 ?? 0} />
          </Box>

          <Box sx={{ background: `linear-gradient(135deg, ${TEAL}08, ${PURPLE}08)`, borderRadius: 2.5, border: `1px solid ${PURPLE}26`, p: 2.2, minWidth: 0, overflowWrap: "anywhere" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1 }}>
              <BoltRoundedIcon sx={{ fontSize: 15, color: PURPLE }} />
              <Typography sx={{ fontSize: "0.66rem", color: PURPLE, textTransform: "uppercase", letterSpacing: "0.09em", fontWeight: 800, fontFamily: FONT }}>
                Fund Manager View
              </Typography>
            </Box>
            <Typography sx={{ fontSize: "0.82rem", color: T1, fontWeight: 600, lineHeight: 1.65, fontFamily: FONT, mb: 1.6 }}>
              {formatDisplayText(ai?.fund_manager_summary, "")}
            </Typography>

            <Box sx={{ borderTop: `1px solid ${BORDER}`, pt: 1.2, mb: 1.2 }}>
              <Typography sx={{ fontSize: "0.62rem", color: T3, textTransform: "uppercase", letterSpacing: "0.09em", fontWeight: 800, fontFamily: FONT, mb: 0.4 }}>
                Short-Term View
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: T1, fontWeight: 600, lineHeight: 1.55, fontFamily: FONT }}>
                {formatDisplayText(ai?.short_term_view, "")}
              </Typography>
            </Box>

            <Box sx={{ borderTop: `1px solid ${BORDER}`, pt: 1.2 }}>
              <Typography sx={{ fontSize: "0.62rem", color: T3, textTransform: "uppercase", letterSpacing: "0.09em", fontWeight: 800, fontFamily: FONT, mb: 0.4 }}>
                Trade Bias
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", color: sigCol(ts?.trade_bias || ""), fontWeight: 900, fontFamily: FONT }}>
                {formatDisplayText(ts?.trade_bias, "")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default IPOQuantDashboardRenderer;
